import { useEffect } from "react";
import "vanilla-cookieconsent/dist/cookieconsent.css";
import * as CookieConsent from "vanilla-cookieconsent";

import { DEFAULT_LOCALE, SUPPORTED_LOCALES } from "constants/i18n";
import { COMMON_COPY } from "copy/common";
import { useActiveLocale } from "i18n/activeLocale";
import { createCopyResolver } from "i18n/resolveCopy";
import { pushCookieConsentUpdateDataLayerEvent } from "lib/gtmDataLayer";
import { loadXPixelIfConsented } from "lib/xPixel";
import { type ICopyResolver } from "types/i18n/ICopyResolver";

function updateConsentBasedTracking(): void {
  const analyticsGranted = CookieConsent.acceptedCategory("analytics");
  const marketingGranted = CookieConsent.acceptedCategory("marketing");

  window.gtag?.("consent", "update", {
    analytics_storage: analyticsGranted ? "granted" : "denied",
    ad_storage: marketingGranted ? "granted" : "denied",
    ad_user_data: marketingGranted ? "granted" : "denied",
    ad_personalization: marketingGranted ? "granted" : "denied",
  });

  pushCookieConsentUpdateDataLayerEvent({
    analyticsGranted: analyticsGranted,
    marketingGranted: marketingGranted,
  });

  loadXPixelIfConsented({ marketingGranted: marketingGranted });
}

function buildConsentTranslation({ copy }: { copy: ICopyResolver }): CookieConsent.Translation {
  return {
    consentModal: {
      title: copy.text(COMMON_COPY.cookieConsent.consentModal.title),
      description: copy.text(COMMON_COPY.cookieConsent.consentModal.description),
      acceptAllBtn: copy.text(COMMON_COPY.cookieConsent.acceptAll),
      acceptNecessaryBtn: copy.text(COMMON_COPY.cookieConsent.rejectAll),
      showPreferencesBtn: copy.text(COMMON_COPY.cookieConsent.consentModal.managePreferences),
    },
    preferencesModal: {
      title: copy.text(COMMON_COPY.cookieConsent.preferencesModal.title),
      acceptAllBtn: copy.text(COMMON_COPY.cookieConsent.acceptAll),
      acceptNecessaryBtn: copy.text(COMMON_COPY.cookieConsent.rejectAll),
      savePreferencesBtn: copy.text(COMMON_COPY.cookieConsent.preferencesModal.savePreferences),
      sections: [
        {
          title: copy.text(COMMON_COPY.cookieConsent.categories.necessary.title),
          description: copy.text(COMMON_COPY.cookieConsent.categories.necessary.description),
          linkedCategory: "necessary",
        },
        {
          title: copy.text(COMMON_COPY.cookieConsent.categories.analytics.title),
          description: copy.text(COMMON_COPY.cookieConsent.categories.analytics.description),
          linkedCategory: "analytics",
        },
        {
          title: copy.text(COMMON_COPY.cookieConsent.categories.marketing.title),
          description: copy.text(COMMON_COPY.cookieConsent.categories.marketing.description),
          linkedCategory: "marketing",
        },
      ],
    },
  };
}

export function showCookieSettings(): void {
  CookieConsent.showPreferences();
}

/** Starts the cookie consent banner once and keeps its copy in the active UI language. */
export default function CookieConsentBanner(): null {
  const activeLocale = useActiveLocale();

  useEffect(() => {
    CookieConsent.run({
      categories: {
        necessary: { enabled: true, readOnly: true },
        analytics: {},
        marketing: {},
      },
      cookie: {
        sameSite: "Lax",
      },
      onFirstConsent: updateConsentBasedTracking,
      onConsent: updateConsentBasedTracking,
      onChange: updateConsentBasedTracking,
      language: {
        default: DEFAULT_LOCALE,
        // <html lang> follows the active locale, so the banner first opens in the page's language.
        autoDetect: "document",
        translations: Object.fromEntries(
          SUPPORTED_LOCALES.map((locale) => [
            locale,
            () => buildConsentTranslation({ copy: createCopyResolver({ locale: locale }) }),
          ])
        ),
      },
    });
  }, []);

  useEffect(() => {
    // Re-renders an open banner or preferences modal in the language the visitor just picked.
    void CookieConsent.setLanguage(activeLocale);
  }, [activeLocale]);

  return null;
}
